import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const DUMMY_SAVED_FILTERS = {
  view_type: null,
  calendar_view: "month",
  agenda_view: false,
  time_range_from: null,
  time_range_to: null,
  profile_ids: [],
  boat_ids: [],
  category_ids: [],
  experience_ids: [],
  role_ids: [],
  unassigned: false,
  crew_unassigned: false,
  equipment_unassigned: false
}

export const normalizeCalendarFilters = (filters = {}) => {
  const fallbackBoatIds = Array.isArray(filters?.equipment_ids) ? filters.equipment_ids : []
  const boatIds = Array.isArray(filters?.boat_ids) ? filters.boat_ids : fallbackBoatIds
  return {
    view_type: filters?.view_type ?? null,
    calendar_view: filters?.calendar_view || "month",
    agenda_view: Boolean(filters?.agenda_view),
    time_range_from: filters?.time_range_from || null,
    time_range_to: filters?.time_range_to || null,
    search: filters?.search || "",
    profile_ids: Array.isArray(filters?.profile_ids) ? filters.profile_ids : [],
    boat_ids: boatIds,
    category_ids: Array.isArray(filters?.category_ids) ? filters.category_ids : [],
    experience_ids: Array.isArray(filters?.experience_ids) ? filters.experience_ids : [],
    role_ids: Array.isArray(filters?.role_ids) ? filters.role_ids : [],
    unassigned: Boolean(filters?.unassigned),
    crew_unassigned: Boolean(filters?.crew_unassigned),
    equipment_unassigned: Boolean(filters?.equipment_unassigned)
  }
}

// ACTIONS
const calendarFiltersRequested = createAction("calendar/filters/requested")
const calendarFiltersReceived = createAction("calendar/filters/received")
const calendarFiltersFailed = createAction("calendar/filters/failed")
const calendarFiltersSaved = createAction("calendar/filters/saved")
const calendarFiltersReset = createAction("calendar/filters/reset")

const calendarBookingRequested = createAction("calendar/booking/requested")
const calendarBookingReceived = createAction("calendar/booking/received")
const calendarBookingFailed = createAction("calendar/booking/failed")

const calendarTripLocationsRequested = createAction("calendar/trip_locations/requested")
const calendarTripLocationsReceived = createAction("calendar/trip_locations/received")
const calendarTripLocationsFailed = createAction("calendar/trip_locations/failed")

const calendarJournalNoteRequested = createAction("calendar/journal_note/requested")
const calendarJournalNoteReceived = createAction("calendar/journal_note/received")
const calendarJournalNoteFailed = createAction("calendar/journal_note/failed")

const calendarJournalCreateNoteRequested = createAction("calendar/journal_note/create_note/requested")
const calendarJournalCreateNoteReceived = createAction("calendar/journal_note/create_note/received")
const calendarJournalCreateNoteFailed = createAction("calendar/journal_note/create_note/failed")

const calendarJournalUpdateNoteRequested = createAction("calendar/journal_note/update_note/requested")
const calendarJournalUpdateNoteReceived = createAction("calendar/journal_note/update_note/received")
const calendarJournalUpdateNoteFailed = createAction("calendar/journal_note/update_note/failed")

const calendarJournalVersionsRequested = createAction("calendar/journal_note/versions/requested")
const calendarJournalVersionsReceived = createAction("calendar/journal_note/versions/received")
const calendarJournalVersionsFailed = createAction("calendar/journal_note/versions/failed")

const calendarEquipmentRequested = createAction("calendar/equipment/requested")
const calendarEquipmentReceived = createAction("calendar/equipment/received")
const calendarEquipmentFailed = createAction("calendar/equipment/failed")

const calendarTimeOffDeleteRequested = createAction("calendar/time_off/delete/requested")
const calendarTimeOffDeleteReceived = createAction("calendar/time_off/delete/received")
const calendarTimeOffDeleteFailed = createAction("calendar/time_off/delete/failed")

const calendarTimeOffCreateRequested = createAction("calendar/time_off/create/requested")
const calendarTimeOffCreateReceived = createAction("calendar/time_off/create/received")
const calendarTimeOffCreateFailed = createAction("calendar/time_off/create/failed")

const calendarTimeOffRequested = createAction("calendar/time_off/requested")
const calendarTimeOffReceived = createAction("calendar/time_off/received")
const calendarTimeOffFailed = createAction("calendar/time_off/failed")

const calendarTimeOffUpdateRequested = createAction("calendar/time_off/update/requested")
const calendarTimeOffUpdateReceived = createAction("calendar/time_off/update/received")
const calendarTimeOffUpdateFailed = createAction("calendar/time_off/update/failed")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  bookings: [],
  calendarEquipment: [],
  daysEmpty: false,
  savedFilters: DUMMY_SAVED_FILTERS,
  filtersReady: false,
  hasMyBookings: true,
  calendarTimeOff: [],
  journalNoteDates: [],
  tripLocations: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  journalNote: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  journalVersions: {
    ...LOADING_INITIAL_STATE,
    data: {
      versions: [],
      pagination: null
    }
  },
  timeOff: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  timeOffSave: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  timeOffUpdate: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  timeOffDelete: {
    ...LOADING_INITIAL_STATE,
    data: null
  }
}

const calendarReducer = createReducer(initialState, {
  [calendarEquipmentRequested.type]: () => {},
  [calendarEquipmentReceived.type]: (state, action) => {
    state.calendarEquipment = action.payload?.data || []
  },
  [calendarEquipmentFailed.type]: () => {},
  [calendarFiltersRequested.type]: () => {},
  [calendarFiltersReceived.type]: (state, action) => {
    const raw = action.payload?.data?.saved_filters || action.payload?.saved_filters
    if (!raw) {
      state.filtersReady = true
      return
    }
    const normalized = normalizeCalendarFilters(raw)
    state.savedFilters = normalized
    state.filtersReady = true
  },
  [calendarFiltersFailed.type]: (state) => {
    state.filtersReady = true
  },
  [calendarFiltersReset.type]: (state) => {
    state.filtersReady = false
  },
  [calendarFiltersSaved.type]: (state, action) => {
    state.savedFilters = normalizeCalendarFilters(action.payload)
  },
  [calendarBookingRequested.type]: (state) => {
    setRequested(state)
    state.bookings = []
  },
  [calendarBookingReceived.type]: (state, action) => {
    setReceived(state)
    const data = action.payload?.data
    state.bookings = data?.bookings ?? data?.days?.flatMap((d) => d.bookings ?? []) ?? []
    state.daysEmpty = Array.isArray(data?.days) && data.days.length === 0
    state.journalNoteDates = data?.journal_note_dates || []
    state.hasMyBookings = typeof data?.has_bookings === "boolean" ? data.has_bookings : state.hasMyBookings
    if (data?.time_off) {
      state.calendarTimeOff = data.time_off
    }
  },
  [calendarBookingFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarTripLocationsRequested.type]: (state) => {
    setRequested(state.tripLocations)
    state.tripLocations.data = []
  },
  [calendarTripLocationsReceived.type]: (state, action) => {
    setReceived(state.tripLocations)
    state.tripLocations.data = action.payload?.trip_locations || []
  },
  [calendarTripLocationsFailed.type]: (state) => {
    setFailed(state.tripLocations)
    state.tripLocations.data = []
  },
  [calendarJournalNoteRequested.type]: (state) => {
    setRequested(state.journalNote)
    state.journalNote.data = null
  },
  [calendarJournalNoteReceived.type]: (state, action) => {
    setReceived(state.journalNote)
    state.journalNote.data = action.payload || null
  },
  [calendarJournalNoteFailed.type]: (state) => {
    setFailed(state.journalNote)
    state.journalNote.data = null
  },
  [calendarJournalCreateNoteRequested.type]: (state) => {
    setRequested(state)
  },
  [calendarJournalCreateNoteReceived.type]: (state, action) => {
    setReceived(state)
  },
  [calendarJournalCreateNoteFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarJournalUpdateNoteRequested.type]: (state) => {
    setRequested(state)
  },
  [calendarJournalUpdateNoteReceived.type]: (state, action) => {
    setReceived(state)
  },
  [calendarJournalUpdateNoteFailed.type]: (state) => {
    setFailed(state)
  },
  [calendarJournalVersionsRequested.type]: (state) => {
    setRequested(state.journalVersions)
  },
  [calendarJournalVersionsReceived.type]: (state, action) => {
    setReceived(state.journalVersions)
    state.journalVersions.data = {
      versions: action.payload?.versions || [],
      pagination: action.payload?.pagination || null
    }
  },
  [calendarJournalVersionsFailed.type]: (state) => {
    setFailed(state.journalVersions)
    state.journalVersions.data = {
      versions: [],
      pagination: null
    }
  },
  [calendarTimeOffDeleteRequested.type]: (state) => {
    setRequested(state.timeOffDelete)
  },
  [calendarTimeOffDeleteReceived.type]: (state) => {
    setReceived(state.timeOffDelete)
  },
  [calendarTimeOffDeleteFailed.type]: (state) => {
    setFailed(state.timeOffDelete)
  },
  [calendarTimeOffCreateRequested.type]: (state) => {
    setRequested(state.timeOffSave)
  },
  [calendarTimeOffCreateReceived.type]: (state) => {
    setReceived(state.timeOffSave)
  },
  [calendarTimeOffCreateFailed.type]: (state) => {
    setFailed(state.timeOffSave)
  },
  [calendarTimeOffRequested.type]: (state) => {
    setRequested(state.timeOff)
    state.timeOff.data = []
  },
  [calendarTimeOffReceived.type]: (state, action) => {
    setReceived(state.timeOff)
    const mapped = (action.payload || []).map((item) => ({
      ...item,
      season_start: item.season_start ?? item.start_date,
      season_end: item.season_end ?? item.end_date
    }))
    state.timeOff.data = mapped
    // Only sync to calendarTimeOff when fetching all time off data
    // Don't overwrite when fetching for a specific crew member (modal use case)
    // The calendarTimeOff should only be updated from the bookings API response
  },
  [calendarTimeOffFailed.type]: (state) => {
    setFailed(state.timeOff)
    state.timeOff.data = []
  },
  [calendarTimeOffUpdateRequested.type]: (state) => {
    setRequested(state.timeOffUpdate)
  },
  [calendarTimeOffUpdateReceived.type]: (state) => {
    setReceived(state.timeOffUpdate)
  },
  [calendarTimeOffUpdateFailed.type]: (state) => {
    setFailed(state.timeOffUpdate)
  }
})

export default calendarReducer

// PUBLIC ACTIONS

export const setSavedFilters = calendarFiltersSaved
export const resetCalendarFiltersReady = calendarFiltersReset
export const setCalendarBookingsLoading = calendarBookingRequested

export const getCalendarFilters = () =>
  api({
    url: "/guide/calendar/filters",
    onStart: calendarFiltersRequested.type,
    onSuccess: calendarFiltersReceived.type,
    onError: calendarFiltersFailed.type,
    suppressErrorNotification: true
  })

export const getCalendarBookings = (params) =>
  api({
    url: "/guide/calendar/bookings",
    params,
    onStart: calendarBookingRequested.type,
    onSuccess: calendarBookingReceived.type,
    onError: calendarBookingFailed.type
  })

export const updateCalendarFilters = (data) =>
  api({
    url: "/guide/calendar/update_filters",
    method: "patch",
    data,
    suppressErrorNotification: true
  })

export const updateCalendarFiltersAndSync = (data) =>
  api({
    url: "/guide/calendar/update_filters",
    method: "patch",
    data,
    onSuccess: calendarFiltersReceived.type,
    suppressErrorNotification: true
  })

export const updateAndRefreshCalendarFilters = (data) => (dispatch) =>
  dispatch(updateCalendarFilters(data))
    .then(() => dispatch(getCalendarFilters()))
    .catch(() => {})

export const getCalendarTripLocations = () =>
  api({
    url: "/guide/calendar/guide_trip_locations",
    onStart: calendarTripLocationsRequested.type,
    onSuccess: calendarTripLocationsReceived.type,
    onError: calendarTripLocationsFailed.type
  })

export const getCalendarJournalNote = (date) =>
  api({
    url: `/guide/journal_notes?date=${date}`,
    onStart: calendarJournalNoteRequested.type,
    onSuccess: calendarJournalNoteReceived.type,
    onError: calendarJournalNoteFailed.type
  })

export const createCalendarJournal = (data) =>
  api({
    url: "/guide/journal_notes",
    method: "post",
    onStart: calendarJournalCreateNoteRequested.type,
    onSuccess: calendarJournalCreateNoteReceived.type,
    onError: calendarJournalCreateNoteFailed.type,
    data
  })

export const updateCalendarJournal = (id, data) =>
  api({
    url: `/guide/journal_notes/${id}`,
    method: "put",
    data: data,
    onStart: calendarJournalUpdateNoteRequested.type,
    onSuccess: calendarJournalUpdateNoteReceived.type,
    onError: calendarJournalUpdateNoteFailed.type
  })

export const createCalendarTimeOff = (data, profileId, suppressErrorNotification = false) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities` : "/guide/availabilities",
    method: "post",
    onStart: calendarTimeOffCreateRequested.type,
    onSuccess: calendarTimeOffCreateReceived.type,
    onError: calendarTimeOffCreateFailed.type,
    suppressErrorNotification,
    data: { availability: data }
  })

export const getCalendarTimeOff = (params, profileId) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities` : "/guide/availabilities",
    params: { ...params },
    onStart: calendarTimeOffRequested.type,
    onSuccess: calendarTimeOffReceived.type,
    onError: calendarTimeOffFailed.type
  })

export const deleteTimeOff = (id, profileId) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities/${id}` : `/guide/availabilities/${id}`,
    method: "delete",
    onStart: calendarTimeOffDeleteRequested.type,
    onSuccess: calendarTimeOffDeleteReceived.type,
    onError: calendarTimeOffDeleteFailed.type
  })

export const updateCalendarTimeOff = (id, data, profileId, suppressErrorNotification = false) =>
  api({
    url: profileId ? `/guide/profiles/${profileId}/availabilities/${id}` : `/guide/availabilities/${id}`,
    method: "put",
    data: { availability: data },
    onStart: calendarTimeOffUpdateRequested.type,
    onSuccess: calendarTimeOffUpdateReceived.type,
    onError: calendarTimeOffUpdateFailed.type,
    suppressErrorNotification
  })

export const saveCalendarJournal = (id, data) => (id ? updateCalendarJournal(id, data) : createCalendarJournal(data))

export const getCalendarEquipment = () =>
  api({
    url: "/guide/boats/listing",
    params: { lang: "en-GB" },
    onStart: calendarEquipmentRequested.type,
    onSuccess: calendarEquipmentReceived.type,
    onError: calendarEquipmentFailed.type
  })

export const getCalendarEquipmentCategories = () =>
  api({
    url: "/guide/boats/category_to_boats",
    params: { lang: "en-GB" }
  })

export const getCalendarProfiles = () =>
  api({
    url: "/guide/profiles",
    params: { lang: "en-GB" }
  })

export const getCalendarExperiences = () =>
  api({
    url: "/guide/trips/embedded_experiences_list",
    params: { list_for: "calendar", lang: "en-GB" }
  })

export const getJournalVersions = (id, params = {}) =>
  api({
    url: `/guide/journal_notes/${id}/versions`,
    params: { ...params },
    onStart: calendarJournalVersionsRequested.type,
    onSuccess: calendarJournalVersionsReceived.type,
    onError: calendarJournalVersionsFailed.type
  })
